'use client';

import React from 'react';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className = '', ...props }) => {
  return (
    <div
      className={`relative overflow-hidden bg-[#206A4F]/10 dark:bg-white/10 rounded-2xl ${className}`}
      {...props}
    >
      {/* Liquid Luxury Shimmer Effect */}
      <div className="absolute inset-0 translate-x-[-100%] animate-shimmer bg-gradient-to-r from-transparent via-white/40 dark:via-white/20 to-transparent pointer-events-none" />
    </div>
  );
};

// ---------------------------------------------------------
// 1. Articles Grid Skeleton (For /articles)
// ---------------------------------------------------------
export const ArticlesGridSkeleton: React.FC = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Large Featured Card Skeleton */}
      <div className="md:col-span-2 lg:col-span-2 row-span-2 bg-white dark:bg-white/5 rounded-3xl p-8 border border-[#206A4F]/10 shadow-sm flex flex-col justify-between min-h-[380px]">
        <div>
          <div className="flex items-center justify-between mb-6">
            <Skeleton className="h-5 w-32 rounded-lg" />
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
          <Skeleton className="h-10 md:h-12 w-3/4 mb-6 rounded-2xl" />
          <Skeleton className="h-4 w-full mb-3" />
          <Skeleton className="h-4 w-5/6 mb-3" />
          <Skeleton className="h-4 w-2/3 mb-8" />
        </div>
        <div className="mt-auto flex items-center justify-between border-t border-[#206A4F]/10 pt-6">
          <Skeleton className="h-11 w-11 rounded-full" />
          <Skeleton className="h-11 w-11 rounded-full" />
        </div>
      </div>

      {/* Regular Cards Skeleton */}
      {[1, 2, 3, 4].map((item) => (
        <div key={item} className="bg-white dark:bg-white/5 rounded-3xl p-8 border border-[#206A4F]/10 shadow-sm flex flex-col justify-between min-h-[300px]">
          <div>
            <div className="flex items-center justify-between mb-6">
              <Skeleton className="h-4 w-24 rounded-md" />
            </div>
            <Skeleton className="h-7 w-4/5 mb-4 rounded-xl" />
            <Skeleton className="h-3.5 w-full mb-2" />
            <Skeleton className="h-3.5 w-3/4 mb-6" />
          </div>
          <div className="mt-auto flex items-center justify-between border-t border-[#206A4F]/10 pt-6">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-10 w-10 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------
// 2. Article Detail Skeleton (For /articles/[slug])
// ---------------------------------------------------------
export const ArticleDetailSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#F5F0E8] dark:bg-[#121A16] pt-24 pb-32 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Top bar back button placeholder */}
        <div className="flex items-center justify-between mb-10">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-10 w-10 rounded-full" />
        </div>

        {/* Title and Meta Header */}
        <div className="text-center mb-16 flex flex-col items-center">
          <Skeleton className="h-12 md:h-16 w-5/6 mb-6 rounded-2xl" />
          <Skeleton className="h-8 w-3/4 mb-8 rounded-xl" />
          <div className="flex items-center justify-center gap-4 border-y border-[#206A4F]/10 py-5 w-full max-w-lg">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-5 w-20" />
          </div>
        </div>

        {/* Content paragraphs */}
        <div className="space-y-6 mb-20 bg-white/50 dark:bg-white/5 p-8 md:p-12 rounded-3xl border border-[#206A4F]/10">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-11/12" />
          <Skeleton className="h-5 w-4/5 mb-4" />

          <Skeleton className="h-64 w-full rounded-2xl my-8" />

          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-10/12" />
          <Skeleton className="h-5 w-5/6" />
          <Skeleton className="h-5 w-3/4" />
        </div>

        {/* Comments Section Skeleton */}
        <div className="bg-[#F0F7F3] dark:bg-white/5 rounded-3xl p-8 md:p-12 border border-[#206A4F]/10">
          <Skeleton className="h-8 w-40 mb-8 rounded-xl" />
          <div className="space-y-4 mb-8">
            <Skeleton className="h-12 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-2xl" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-24 w-full rounded-2xl" />
            <Skeleton className="h-24 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------
// 3. Admin Dashboard Skeleton (For /admin)
// ---------------------------------------------------------
export const AdminDashboardSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#F5F0E8] dark:bg-[#121A16] pb-24 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        <Skeleton className="h-12 w-64 mb-10 rounded-2xl" />

        {/* KPI Scorecards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {[1, 2, 3].map((idx) => (
            <div key={idx} className="bg-white dark:bg-white/5 p-6 rounded-3xl border border-[#206A4F]/10 shadow-sm flex items-center gap-4">
              <Skeleton className="w-16 h-16 rounded-2xl shrink-0" />
              <div className="flex-1">
                <Skeleton className="h-4 w-28 mb-2" />
                <Skeleton className="h-8 w-20 rounded-lg" />
              </div>
            </div>
          ))}
        </div>

        {/* Comments section & stats container */}
        <div className="bg-white dark:bg-white/5 rounded-3xl p-8 border border-[#206A4F]/10 mb-12 min-h-[350px]">
          <Skeleton className="h-8 w-48 mb-6 rounded-xl" />
          <div className="space-y-4">
            {[1, 2, 3].map((c) => (
              <div key={c} className="p-4 rounded-2xl border border-[#206A4F]/10 bg-[#F0F7F3]/50 dark:bg-white/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-4 w-3/4" />
                </div>
                <div className="flex gap-2">
                  <Skeleton className="w-24 h-10 rounded-xl" />
                  <Skeleton className="w-24 h-10 rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Volunteers Grid Skeleton */}
        <div className="bg-white dark:bg-white/5 rounded-3xl p-8 border border-[#206A4F]/10 min-h-[400px]">
          <Skeleton className="h-8 w-52 mb-6 rounded-xl" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((v) => (
              <div key={v} className="p-5 rounded-2xl border border-[#206A4F]/10 bg-[#F0F7F3]/50 dark:bg-white/5 space-y-4">
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-16 w-full rounded-xl" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------
// 4. Volunteer Form Skeleton (For /volunteer-form Suspense)
// ---------------------------------------------------------
export const VolunteerFormSkeleton: React.FC = () => {
  return (
    <div className="w-full max-w-3xl bg-[#F5F0E8]/90 dark:bg-[#1A2620]/90 backdrop-blur-xl border border-[#206A4F]/20 rounded-[2rem] p-8 md:p-12 shadow-2xl space-y-8">
      <div className="flex flex-col items-center justify-center gap-3">
        <Skeleton className="w-16 h-16 rounded-full" />
        <Skeleton className="h-8 w-64 rounded-xl" />
        <Skeleton className="h-4 w-80 max-w-full" />
      </div>
      
      <div className="space-y-6 pt-4">
        <Skeleton className="h-14 w-full rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-14 w-full rounded-xl" />
          <Skeleton className="h-14 w-full rounded-xl" />
        </div>
        <Skeleton className="h-14 w-full rounded-xl" />
      </div>

      <div className="space-y-3">
        <Skeleton className="h-5 w-40" />
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-xl" />
          ))}
        </div>
      </div>

      <Skeleton className="h-14 w-full rounded-xl mt-8" />
    </div>
  );
};
